import KeyHandler from '@/components/KeyHandler';
import ModalV2 from '@/components/ModalV2';
import useChangeCompany from '@/hooks/useChangeCompany';
import useDebounce from '@/hooks/useDebounce';
import { checkCompanyName, postCompany } from '@/redux/actions/settingsActions';
import { setModalState } from '@/redux/actions/uiActions/modalActions';
import { getUserCompanies, getUserCompaniesOptions } from '@/redux/actions/userActions';
import memoizeSelector from '@/redux/lib/memoizeSelector';
import selectors from '@/redux/selectors/selectors';
import { modal } from '@/redux/selectors/uiSelectors';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import OrganizationPane from './inviteMemberModal/OrganizationPane';

const CreateOrganizationModal = () => {
  const [isSaving, setIsSaving] = useState(false);
  const [state, setState] = useState({});
  const [isValidName, setIsValidName] = useState(false);
  const [isLoadingCheck, setIsLoadingCheck] = useState(false);
  const modalState = useSelector(memoizeSelector('modal:createOrganization', (state) => modal(state, 'createOrganization')));
  const userId = useSelector(selectors.userId);
  const show = modalState?.open;
  const changeCompany = useChangeCompany();

  const handleClose = () => {
    setIsSaving(false);
    setState({});
    setModalState('createOrganization', { open: false });
  };

  const checkName = async () => {
    if (state?.name) {
      const nameCheck = await checkCompanyName({ name: state?.name });
      setIsValidName(nameCheck?.isValid);
    }
    setIsLoadingCheck(false);
  };

  const debouncedChange = useDebounce(checkName, 500, []);

  const handleChange = (key, value) => {
    setIsValidName(true);
    setIsLoadingCheck(true);
    setState({ ...state, [key]: value });
  };

  const handleCreate = async () => {
    setIsSaving(true);
    await postCompany({ name: state.name, ownerId: userId }, async (response) => {
      const activeCompanyId = response?.activeCompanyId;
      getUserCompanies({
        userId,
        callback: () => {
          changeCompany(activeCompanyId, { redirect: true });
        }
      });
    });
    handleClose();
    getUserCompanies({ userId });
    getUserCompaniesOptions({ userId });
    setIsSaving(false);
  };

  const keyMap = { Enter: handleCreate };
  const actions = [{ children: 'Create', onClick: handleCreate, className: 'primary', disabled: !state.name || !isValidName || isLoadingCheck, loading: isSaving }];

  const footerText = useMemo(() => {
    if (!state.name) return 'Please fill in the name.';
    if (isLoadingCheck) return 'Checking name...';
    if (!isValidName) return 'This name is already being used.';
    return 'This name is valid.';
  }, [state.name, isLoadingCheck, isValidName]);

  useEffect(() => {
    debouncedChange();
    return () => {
      debouncedChange();
    };
  }, [state.name]);

  return (
    <ModalV2
      maxWidth={'400px'} //
      className={'create-organization-modal'}
      show={show}
      onHide={handleClose}
      title={'Create Organization'}
      actions={actions}
      footer={footerText}
      autoFocusRef={'input'}>
      <div className={'wrapper'}>
        <OrganizationPane value={state.name} onChange={handleChange} />
      </div>
      <KeyHandler keyMap={keyMap} global={true} preventDefault={true} stopPropagation={true} />
    </ModalV2>
  );
};

export default CreateOrganizationModal;
